import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { resolveSaveErrorMessage } from "../lib/settingsErrorMessages";
import { useAuth } from "../context/AuthContext";
import { ActionButton } from "./kit";
import SettingsSheet from "./SettingsSheet";
import SettingsPhotoControl from "./SettingsPhotoControl";

const DISPLAY_NAME_MAX = 60;
const MOBILE_PHONE_MAX = 40;

const FIELD_STYLE = {
  minHeight: 46,
  padding: "0 12px",
  background: "var(--nw-surface)",
  border: "1px solid var(--nw-line)",
  borderRadius: 10,
  fontSize: 15,
  color: "var(--nw-ink)",
  width: "100%",
  boxSizing: "border-box",
};

/** D55/F8: a field-level refusal, a warning triangle, weight 600, ink. */
function FieldError({ id, testId, children }) {
  return (
    <p
      id={id}
      data-testid={testId}
      style={{ display: "flex", alignItems: "flex-start", gap: 6, fontSize: 13, lineHeight: 1.45, fontWeight: 600, color: "var(--nw-ink)", margin: 0 }}
    >
      <span style={{ display: "inline-flex", flexShrink: 0, marginTop: 1 }}>
        <AlertTriangle size={15} color="#B42318" />
      </span>
      <span>{children}</span>
    </p>
  );
}

/**
 * Edit your details (#1135, the approved SettingsPlayer and SettingsCoach
 * boards): display name, mobile phone, the photo, and ONE gold Save. It
 * replaces the three saves the old account card carried (name, number, photo).
 *
 * One Save sends one `PUT /auth/me` carrying `display_name` and
 * `mobile_phone` (the route has taken both together since #456). The photo
 * uploads the moment it is picked (`SettingsPhotoControl`). The validation is
 * the old card's, moved here: a required display name of 60 characters or
 * fewer, a mobile number of 40 or fewer. A failure stays in the sheet (D57).
 */
function DetailsForm({ onDone, isCoach }) {
  const { user, updateUser } = useAuth();
  const [displayName, setDisplayName] = useState(user?.display_name || "");
  const [mobilePhone, setMobilePhone] = useState(user?.mobile_phone || "");
  const [nameError, setNameError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [saving, setSaving] = useState(false);
  const dirty = displayName !== (user?.display_name || "") || mobilePhone !== (user?.mobile_phone || "");

  const save = async () => {
    const name = displayName.trim();
    const phone = mobilePhone.trim();
    let bad = false;
    if (!name) {
      setNameError("Display name is required.");
      bad = true;
    } else if (name.length > DISPLAY_NAME_MAX) {
      setNameError(`Display name must be ${DISPLAY_NAME_MAX} characters or fewer.`);
      bad = true;
    } else setNameError("");
    if (phone.length > MOBILE_PHONE_MAX) {
      setPhoneError(`Mobile number must be ${MOBILE_PHONE_MAX} characters or fewer.`);
      bad = true;
    } else setPhoneError("");
    if (bad) return;

    setSaving(true);
    try {
      const { data } = await api.put("/auth/me", { display_name: name, mobile_phone: phone });
      updateUser(data);
      toast.success("Details updated");
      onDone();
    } catch (e) {
      // D57: the failure stays in the sheet and does not also toast. A refusal
      // that names the number shows under Phone (F6), any other under Display name.
      const message = resolveSaveErrorMessage(e);
      if (/mobile|phone/i.test(message)) setPhoneError(message);
      else setNameError(message);
    }
    setSaving(false);
  };

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <label htmlFor="settings-display-name" style={{ fontSize: 11, fontWeight: 600, color: "var(--nw-ink-muted)" }}>
          Display name
        </label>
        <input
          id="settings-display-name"
          data-testid="settings-display-name"
          value={displayName}
          onChange={(e) => { setDisplayName(e.target.value); if (nameError) setNameError(""); }}
          maxLength={200}
          style={FIELD_STYLE}
          aria-invalid={nameError ? "true" : undefined}
          aria-describedby={nameError ? "settings-display-name-error" : undefined}
        />
        {nameError && (
          <FieldError id="settings-display-name-error" testId="settings-display-name-error">
            {nameError}
          </FieldError>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <label htmlFor="settings-mobile-phone" style={{ fontSize: 11, fontWeight: 600, color: "var(--nw-ink-muted)" }}>
          Mobile phone
        </label>
        <input
          id="settings-mobile-phone"
          data-testid="settings-mobile-phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="+64 21 555 0000"
          value={mobilePhone}
          onChange={(e) => { setMobilePhone(e.target.value); if (phoneError) setPhoneError(""); }}
          maxLength={MOBILE_PHONE_MAX}
          style={FIELD_STYLE}
          aria-invalid={phoneError ? "true" : undefined}
          aria-describedby={phoneError ? "settings-mobile-phone-error" : "settings-mobile-phone-help"}
        />
        <p id="settings-mobile-phone-help" style={{ fontSize: 12, color: "var(--nw-ink-muted)", margin: 0 }}>
          {/* D195 (#989 round 2, screen F11): a coach's page has no Your coach card, so the clause that points at it is dropped. */}
          {isCoach
            ? "Start with your country code, e.g. +64 21 555 0000. Only you can see this. It isn't shown to your coach or to anyone else, and we don't send text messages."
            : "Start with your country code, e.g. +64 21 555 0000. Only you can see this. It isn't shown to your coach or to anyone else, and we don't send text messages — to reach a coach, use Your coach under More."}
        </p>
        {phoneError && (
          <FieldError id="settings-mobile-phone-error" testId="settings-mobile-phone-error">
            {phoneError}
          </FieldError>
        )}
      </div>

      <SettingsPhotoControl />

      <ActionButton onClick={save} disabled={saving || !dirty} data-testid="settings-save-details">
        {saving ? "Saving…" : "Save"}
      </ActionButton>
    </>
  );
}

export default function SettingsDetailsSheet({ open, onOpenChange }) {
  const { user } = useAuth();
  return (
    <SettingsSheet open={open} onOpenChange={onOpenChange} title="Edit your details" data-testid="settings-details-sheet">
      <DetailsForm isCoach={user?.role === "coach"} onDone={() => onOpenChange(false)} />
    </SettingsSheet>
  );
}
